import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Heading, Span } from "@icg360/design-system";

import { MSSDrawer, MSSDrawerHeader } from "components/mss-drawer";
import { useNotificationContext } from "components/notifications/context";
import styles from "components/notifications/notifications-drawer/notifications-drawer.module.scss";
import CheckIcon from "images/icon-check.svg?react";

type NotificationDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
};

type DrawerView = "notificationsPresent" | "noNotifications";

export const NotificationsDrawer = ({
  isOpen,
  onClose,
}: NotificationDrawerProps) => {
  const { notifications, total, dismissAllNotifications } =
    useNotificationContext();
  const [view, setView] = useState<DrawerView>("notificationsPresent");

  const navigate = useNavigate();

  // Reset drawer state when opening
  useEffect(() => {
    if (isOpen) {
      setView(total === 0 ? "noNotifications" : "notificationsPresent");
    }
  }, [isOpen, total]);

  const renderNotificationsList = () => (
    <div className={styles.notificationsGroup}>
      {notifications?.map((notification) => {
        const notificationTitle = notification.offerTitle ?? "";
        const notificationDescription = notification.offerDescription;
        return (
          <div
            key={notification.offerInternalName}
            className={styles.notificationHolder}
          >
            <div className={styles.notificationContent}>
              <Span size="md" className={styles.notificationTitle}>
                {notificationTitle}
              </Span>
              <Span size="sm" className={styles.notificationDescription}>
                {notificationDescription}
              </Span>
            </div>
            <div className={styles.notificationActions}>
              <Button
                className={styles.notificationChevron}
                icon="CaretRight"
                appearance="tertiary"
                size="xs"
                aria-label={`View ${notificationTitle}`}
                onPress={() => {
                  onClose();
                  if (notification.path) {
                    navigate(notification.path);
                  }
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );

  const drawerTitle = "Notifications";
  const notificationSubheaderMessage =
    total === 0
      ? "No new notifications!"
      : `Here are the ${total} latest update${total === 1 ? "" : "s"}`;

  return (
    <MSSDrawer isOpen={isOpen} onClose={onClose}>
      <div className={styles.mssHeaderSection}>
        <MSSDrawerHeader
          title={drawerTitle}
          onClose={onClose}
          closeButtonTestId="notifications-drawer-switcher-close"
        />

        <h3 className={styles.notificationTitleSubheader}>
          {notificationSubheaderMessage}
        </h3>
      </div>
      {view === "notificationsPresent" ? (
        <>
          <MSSDrawer.Body className={styles.mssBody}>
            <div className={styles.drawerContent}>
              {renderNotificationsList()}
            </div>
          </MSSDrawer.Body>
          <MSSDrawer.Footer
            primaryLabel="Clear All"
            onPrimary={dismissAllNotifications}
            primaryButtonTestId="clear-all-button"
          />
        </>
      ) : (
        <>
          <MSSDrawer.Body className={styles.emptyMssBody}>
            <div className={styles.emptyDrawerContent}>
              <div className={styles.notificationEmptyImage}>
                <CheckIcon className={styles.checkImage} />
              </div>
              <Heading
                size="md"
                tag="h3"
                className={styles.notificationsEmptyState}
              >
                Notifications Empty!
              </Heading>
              <Span size="sm" className={styles.emptyNotificationsDescription}>
                No new notifications
              </Span>
            </div>
          </MSSDrawer.Body>
          <MSSDrawer.Footer
            primaryLabel="Clear All"
            primaryDisabled={true}
            onPrimary={dismissAllNotifications}
            primaryButtonTestId="clear-all-button"
          />
        </>
      )}
    </MSSDrawer>
  );
};
